import { useEffect, useRef, useState, useMemo } from "react";
import { CONFETTI_DURATION_MS, CONFETTI_COUNT, THUMBS_COUNT } from "../../constants/animations";

// ── Streak Yourself — interactive mini-game (gameboard overlay) ─────────────
// Renders as a fixed overlay positioned over the diamond-field element
// (same FIELD_SELECTOR pattern as the sim's StreakerEffect), so the gameboard
// stays visible behind the chase. The initiator pilots the streaker (arrow
// keys + space to sprint, regenerating energy). One guard is the bot-piloted
// "lead" — visually highlighted because in the future the opponent will steer
// it — and 8 AI guards chase identically to the sim. Any guard within
// CATCH_DIST tags the streaker; reaching the exit-edge band escapes.
//
// v1: single-player. Online H2H opponents currently see the sim placeholder
// (we don't stream positions yet).

// ── Field discovery (mirrors sim) ───────────────────────────────────────────
const FIELD_SELECTOR = '[data-testid="diamond-field"]';

// ── Visual constants (mirror StreakerEffect for parity) ─────────────────────
const STREAKER_DIAMETER = 14;
const STREAKER_RADIUS = STREAKER_DIAMETER / 2;
const GUARD_DIAMETER = 10;
const GUARD_RADIUS = GUARD_DIAMETER / 2;
const LEAD_GUARD_DIAMETER = 12;        // slightly larger so the highlighted guard reads as "the one"
const LEAD_GUARD_RADIUS = LEAD_GUARD_DIAMETER / 2;
const CATCH_DIST = 18;                 // px center-to-center — matches sim's CATCH_DIST
// Exit zone depth — the streaker escapes once within this many px of the exit
// edge. Kept shallow so the amber band hugs the edge (you must commit to the
// edge to escape, not just drift near it).
const EXIT_BAND_PX = 20;

// Butt-dot constants (cosmetic — lifted from StreakerEffect)
const BUTT_DOT_SCALE     = 0.7;
const BUTT_DOT_PROTRUDE  = 1.5;
const BUTT_DOT_SPREAD    = 2.5;
const BUTT_DOT_SIZE_PX   = Math.round(STREAKER_DIAMETER * BUTT_DOT_SCALE);
const BUTT_DOT_RADIUS    = BUTT_DOT_SIZE_PX / 2;
const BUTT_DOT_BACK_DIST = STREAKER_RADIUS - BUTT_DOT_RADIUS + BUTT_DOT_PROTRUDE;
const BUTT_JIGGLE_FREQ_SLOW = 2.0;
const BUTT_JIGGLE_FREQ_MAX  = 9.0;
const BUTT_JIGGLE_AMP_SLOW  = 0.45;
const BUTT_JIGGLE_AMP_FAST  = 0.12;
const BUTT_JIGGLE_SPEED_REF = 240;

// ── Gameplay speeds ─────────────────────────────────────────────────────────
// Tuned to match the bot-vs-bot sim (StreakerEffect): streaker tops out
// around 100 px/s during normal play with a 200 px/s sprint burst,
// guards push 180–240 px/s. Earlier values (200/320/1400) felt zippy
// next to the sim's lazier pacing.
const STREAKER_BASE_SPEED = 110;
const STREAKER_SPRINT_SPEED = 200;
const STREAKER_ACCEL = 450;
const STREAKER_ENERGY_MAX = 100;
const STREAKER_SPRINT_DRAIN = 50;
const STREAKER_ENERGY_REGEN = 28;

// ── TUNABLE: Lead guard ─────────────────────────────────────────────────────
// The highlighted "lead" guard is the FASTEST on the field and is the ONLY
// guard with a sprint/boost. In v2 this guard will be opponent-controlled —
// keep its top-end strictly above every AI guard's max speed so the player
// can't just outrun the chase trivially. Base speed sits at the top of the
// AI guard speed band; sprint adds the boost on top.
const LEAD_GUARD_BASE_SPEED = 105;
const LEAD_GUARD_SPRINT_SPEED = 145;
const LEAD_GUARD_ACCEL = 200;
const LEAD_GUARD_STAMINA_MAX = 100;
const LEAD_GUARD_STAMINA_DRAIN = 38;
const LEAD_GUARD_SPRINT_RANGE = 300;

// ── TUNABLE: AI guards ──────────────────────────────────────────────────────
// Each AI guard rolls its own speed/accel multiplier at spawn time, so the
// pack doesn't move like a single hivemind. Variance is a fraction (e.g. 0.15
// = ±15%). All multipliers are clamped so no AI exceeds the LEAD guard's base
// speed — the lead is always strictly faster on the field.
const AI_GUARD_COUNT = 8;
const AI_GUARD_SPEED = 88;                // mean px/s before per-guard variance
const AI_GUARD_ACCEL = 160;               // mean accel before per-guard variance
const AI_GUARD_SPEED_VARIANCE = 0.15;     // ±15% per guard
const AI_GUARD_ACCEL_VARIANCE = 0.25;     // ±25% per guard
const AI_GUARD_JITTER = 30;

// Soft cap on the play phase. The clock counts UP, and surviving this long
// counts as a successful escape (great for leaderboards). Bot escalation
// kicks in before this, so reaching the cap should feel earned.
const ROUND_DURATION_MS = 60_000;
const RESULT_HOLD_MS = 2400;

// Bot escalation tiers — every BOT_ESCALATION_INTERVAL_MS of play, AI guards
// get a speed bump and the lead guard's stamina is partly refilled (so they
// get fresh sprint bursts on a long-running chase).
const BOT_ESCALATION_INTERVAL_MS = 10_000;
const BOT_ESCALATION_PER_TIER = 0.10;        // AI guard speed multiplier increment per tier
const LEAD_STAMINA_REFILL_PER_TIER = 40;     // points refilled on each new escalation tier

// Confetti constants imported from ../../constants/animations — shared with
// StreakerEffect so both modes use identical celebration FX timing.
// "Streaker on the field!" pre-streak ready window. The countdown overlay
// renders for READY_MS — during which the entry animation also plays at a
// slowed pace, so the dots arrive just as the countdown hits GO instead of
// snapping into position upfront. Input + end-checks are suppressed for the
// entire window.
const READY_MS = 3000;
// Entry speeds are calibrated against READY_MS — the actors should reach
// roughly their intended on-field positions by the end of the ready phase.
// Roughly: OFF_FIELD_PAD (24px) + ~100 px in / 3 s ≈ 40 px/s for streaker,
// ~30 px/s for guards (slightly slower so they're still closing when control
// transfers, which preserves chase tension on frame 0 of play).
const ENTRY_SPEED_STREAKER = 42;   // px/s
const ENTRY_SPEED_GUARDS   = 30;   // px/s
const OFF_FIELD_PAD = 24;            // px outside the field where actors start

// ── TUNABLE: Exit-edge re-roll ──────────────────────────────────────────────
// Keeps the streaker honest by forcing them to re-route mid-run. The exit band
// visibly slides to a new edge on each switch and the escape check immediately
// starts using the new edge. First switch only fires after the intro completes.
//
// Edit these to taste:
//   * MIN/MAX_MS — how long the exit holds before re-rolling. Wider window =
//     more unpredictable timing. Larger MIN = more breathing room at each exit.
//   * REINFORCEMENT_ON_SWITCH — when the exit switches, spawn one extra AI
//     guard from the side that just CLOSED (the previous exit edge). Adds
//     pressure on the player who was racing toward that edge. Set to false
//     to disable reinforcements entirely.
//   * MAX_REINFORCEMENTS — hard cap on extra guards spawned over a single run.
//     Render slots are pre-reserved up-front, so don't exceed this in tuning
//     without bumping the cap.
const EXIT_SWITCH_MIN_MS = 4000;          // hold ≥ 4s before re-rolling
const EXIT_SWITCH_MAX_MS = 6000;          // up to 6s
const REINFORCEMENT_ON_SWITCH = true;     // spawn a guard from the closed side?
const MAX_REINFORCEMENTS = 6;             // ceiling on extra guards per run

// ── Helpers ─────────────────────────────────────────────────────────────────
function mulberry32(seed) {
  let t = seed >>> 0;
  return function () {
    t = (t + 0x6D2B79F5) >>> 0;
    let r = t;
    r = Math.imul(r ^ (r >>> 15), r | 1);
    r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}
function _len(x, y) { return Math.hypot(x, y); }
function _normalize(x, y) { const m = _len(x, y); return m > 0 ? [x / m, y / m] : [0, 0]; }

// Streaker's off-field spawn — sits just outside the spawn edge so the intro
// animation can carry them onto the field. Mirrors the sim's entry pattern.
function _offFieldSpawnStreaker(spawn_edge, w, h) {
  switch (spawn_edge) {
    case "top":    return { x: w / 2,               y: -OFF_FIELD_PAD };
    case "bottom": return { x: w / 2,               y: h + OFF_FIELD_PAD };
    case "left":   return { x: -OFF_FIELD_PAD,      y: h / 2 };
    case "right":  return { x: w + OFF_FIELD_PAD,   y: h / 2 };
    default:       return { x: w / 2, y: h / 2 };
  }
}

// Inward unit vector × speed for an actor entering from `edge`.
function _inwardVelocity(edge, speed) {
  switch (edge) {
    case "top":    return [0, speed];
    case "bottom": return [0, -speed];
    case "left":   return [speed, 0];
    case "right":  return [-speed, 0];
    default:       return [0, 0];
  }
}

// A random off-field position along `edge`, sampled inside 0.1–0.9 to avoid
// spawning right in the corner. Uses an external rng so different actors get
// different positions without re-seeding.
function _offFieldGuardPos(edge, w, h, rng) {
  const t = 0.1 + 0.8 * rng();
  switch (edge) {
    case "top":    return { x: t * w,               y: -OFF_FIELD_PAD };
    case "bottom": return { x: t * w,               y: h + OFF_FIELD_PAD };
    case "left":   return { x: -OFF_FIELD_PAD,      y: t * h };
    case "right":  return { x: w + OFF_FIELD_PAD,   y: t * h };
    default:       return { x: w / 2, y: -OFF_FIELD_PAD };
  }
}
function _exitVector(exit_edge) {
  switch (exit_edge) {
    case "top":    return [0, -1];
    case "bottom": return [0, 1];
    case "left":   return [-1, 0];
    case "right":  return [1, 0];
    default:       return [0, 0];
  }
}
function _isAtExit(x, y, exit_edge, w, h) {
  switch (exit_edge) {
    case "top":    return y <= EXIT_BAND_PX;
    case "bottom": return y >= h - EXIT_BAND_PX;
    case "left":   return x <= EXIT_BAND_PX;
    case "right":  return x >= w - EXIT_BAND_PX;
    default:       return false;
  }
}
// Guards spawn off-field on the three edges that AREN'T the streaker's spawn
// edge, then glide inward during the intro. Each guard gets an inward velocity
// vector so the sim-like entry animation just works via velocity integration —
// no separate easing curves needed.
// Per-guard speed/accel multiplier sampled at spawn. ±variance from 1.0 so a
// pack of guards doesn't move in lockstep. Centered on 1.0 — mean speed stays
// AI_GUARD_SPEED. Sampled via the passed RNG so the run is deterministic per
// seed.
function _rollGuardMultipliers(rng) {
  return {
    speedMul: 1 + (rng() - 0.5) * 2 * AI_GUARD_SPEED_VARIANCE,
    accelMul: 1 + (rng() - 0.5) * 2 * AI_GUARD_ACCEL_VARIANCE,
  };
}

function _initAiGuards(seed, w, h, spawn_edge) {
  const allEdges = ["top", "right", "bottom", "left"];
  const otherEdges = allEdges.filter(e => e !== spawn_edge);
  const rng = mulberry32(seed);
  const guards = [];
  for (let i = 0; i < AI_GUARD_COUNT; i++) {
    const edge = otherEdges[i % otherEdges.length];
    const pos = _offFieldGuardPos(edge, w, h, rng);
    const [vx, vy] = _inwardVelocity(edge, ENTRY_SPEED_GUARDS);
    const { speedMul, accelMul } = _rollGuardMultipliers(rng);
    guards.push({ x: pos.x, y: pos.y, vx, vy, speedMul, accelMul });
  }
  return guards;
}

// ── Component ───────────────────────────────────────────────────────────────
export default function StreakerInteractiveModal({
  isInitiator,
  spawn_edge,
  exit_edge,
  seed,
  startedAt,
  duration,
  initiatorName,
  onReportResult,
  onClose,
  // Junk Minigames "embedded" mode: the chase runs inside a fixed-size scaled
  // stage (MinigameStage) rather than as an overlay on the live diamond. When
  // set, the field is a synthetic rect from `fieldSize` — no DOM probing — so
  // the run looks and behaves identically at every screen size. Default off ⇒
  // the in-game card path is unchanged.
  embedded = false,
  fieldSize = null,
}) {
  // Track the diamond-field's bounding rect. In card mode this is the live
  // diamond element, re-measured on resize. In embedded mode it is a fixed
  // synthetic rect driven by `fieldSize`.
  const [fieldRect, setFieldRect] = useState(() => {
    if (embedded && fieldSize) {
      return { top: 0, left: 0, width: fieldSize.w, height: fieldSize.h };
    }
    if (typeof document === "undefined") return null;
    const el = document.querySelector(FIELD_SELECTOR);
    return el ? el.getBoundingClientRect() : null;
  });
  useEffect(() => {
    if (embedded) return undefined;   // synthetic rect — nothing to measure
    if (typeof document === "undefined") return undefined;
    const measure = () => {
      const el = document.querySelector(FIELD_SELECTOR);
      setFieldRect(el ? el.getBoundingClientRect() : null);
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [embedded]);

  const dims = useMemo(() => ({
    w: fieldRect ? Math.max(120, fieldRect.width) : 600,
    h: fieldRect ? Math.max(120, fieldRect.height) : 500,
  }), [fieldRect]);

  const [result, setResult] = useState(null);
  // Confetti particles for the result FX. Mirrors StreakerEffect's payload
  // shape exactly so the keyframes + ConfettiParticle component can be
  // shared verbatim.
  const [confetti, setConfetti] = useState(null);
  // Timer counts UP from 0 in the HUD. Soft cap is `ROUND_DURATION_MS`
  // (surviving that long = escape). `duration` from the server is forwarded
  // in the result payload but doesn't drive the HUD anymore.
  const [secondsElapsed, setSecondsElapsed] = useState(0);
  // Ready-phase countdown (3, 2, 1). Drives the "STREAKER ON THE FIELD!"
  // overlay. Cleared when the ready window elapses and play begins.
  const [readyCount, setReadyCount] = useState(() => Math.ceil(READY_MS / 1000));
  const [energyPct, setEnergyPct] = useState(100);
  const [staminaPct, setStaminaPct] = useState(100);
  // The exit edge re-rolls every few seconds during the run. State drives
  // the visual indicators (arrow + exit band); the ref drives the per-frame
  // escape check so the game loop reads the latest value without restarting.
  const [currentExitEdge, setCurrentExitEdge] = useState(exit_edge);
  const currentExitEdgeRef = useRef(exit_edge);
  useEffect(() => { currentExitEdgeRef.current = currentExitEdge; }, [currentExitEdge]);

  const stateRef = useRef(null);
  const inputRef = useRef({ up: false, down: false, left: false, right: false, sprint: false });
  const rafRef = useRef(0);
  const resultRef = useRef(null);

  // Initialise once per run (seed/edges change = new run). Resize doesn't
  // reset — the per-frame clamp absorbs dim changes mid-game. Streaker + all
  // guards spawn OFF the field with inward entry velocities; during the
  // READY_MS pre-streak window the loop integrates them in at those slow
  // velocities without input or end-checks, so the dots arrive ~at GO.
  useEffect(() => {
    const { w, h } = dims;
    // Streaker: off-field on `spawn_edge`, velocity heads inward toward exit.
    const streakerSpawn = _offFieldSpawnStreaker(spawn_edge, w, h);
    const [sVx, sVy] = _inwardVelocity(spawn_edge, ENTRY_SPEED_STREAKER);
    const initialHeading = Math.atan2(sVy, sVx);
    // Lead guard: picks a random non-streaker edge so the player doesn't see
    // it appear from behind. RNG seeded off `seed + 1` so it's deterministic
    // per-run but independent of the AI-guard placement.
    const allEdges = ["top", "right", "bottom", "left"];
    const otherEdges = allEdges.filter(e => e !== spawn_edge);
    const leadRng = mulberry32((seed >>> 0) + 1);
    const leadEdge = otherEdges[Math.floor(leadRng() * otherEdges.length)];
    const leadPos = _offFieldGuardPos(leadEdge, w, h, leadRng);
    const [lVx, lVy] = _inwardVelocity(leadEdge, ENTRY_SPEED_GUARDS);
    stateRef.current = {
      streaker: {
        x: streakerSpawn.x, y: streakerSpawn.y,
        vx: sVx, vy: sVy,
        energy: STREAKER_ENERGY_MAX,
        headingAngle: initialHeading,
        speed: ENTRY_SPEED_STREAKER,
      },
      leadGuard: {
        x: leadPos.x, y: leadPos.y,
        vx: lVx, vy: lVy,
        stamina: LEAD_GUARD_STAMINA_MAX,
      },
      aiGuards: _initAiGuards(seed, w, h, spawn_edge),
      startedAt: startedAt || Date.now(),
      // Latest escalation tier we observed. The loop bumps lead-guard stamina
      // when this increments (every BOT_ESCALATION_INTERVAL_MS of play).
      escalationTier: 0,
    };
    setResult(null);
    resultRef.current = null;
    setCurrentExitEdge(exit_edge);  // reset to the prop on each fresh run
    setEnergyPct(100);
    setStaminaPct(100);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed, spawn_edge, exit_edge]);

  // Keyboard input — initiator only.
  useEffect(() => {
    if (!isInitiator || result) return;
    const handleDown = (e) => {
      if (e.repeat) return;
      switch (e.key) {
        case "ArrowUp": case "w": case "W":    inputRef.current.up = true;    e.preventDefault(); break;
        case "ArrowDown": case "s": case "S":  inputRef.current.down = true;  e.preventDefault(); break;
        case "ArrowLeft": case "a": case "A":  inputRef.current.left = true;  e.preventDefault(); break;
        case "ArrowRight": case "d": case "D": inputRef.current.right = true; e.preventDefault(); break;
        case " ":                              inputRef.current.sprint = true; e.preventDefault(); break;
        default: break;
      }
    };
    const handleUp = (e) => {
      switch (e.key) {
        case "ArrowUp": case "w": case "W":    inputRef.current.up = false; break;
        case "ArrowDown": case "s": case "S":  inputRef.current.down = false; break;
        case "ArrowLeft": case "a": case "A":  inputRef.current.left = false; break;
        case "ArrowRight": case "d": case "D": inputRef.current.right = false; break;
        case " ":                              inputRef.current.sprint = false; break;
        default: break;
      }
    };
    window.addEventListener("keydown", handleDown);
    window.addEventListener("keyup", handleUp);
    return () => {
      window.removeEventListener("keydown", handleDown);
      window.removeEventListener("keyup", handleUp);
    };
  }, [isInitiator, result]);

  // Game loop — initiator only (spectator gets placeholder).
  useEffect(() => {
    if (!isInitiator) return;
    if (result) return;
    if (!fieldRect) return;

    let last = performance.now();
    const startedAtLocal = stateRef.current?.startedAt || Date.now();

    const tick = (now) => {
      const s = stateRef.current;
      if (!s) { rafRef.current = requestAnimationFrame(tick); return; }
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const { w, h } = dims;

      // Two-phase clock:
      //   1. Ready (0 .. READY_MS) — countdown overlay + slow entry slide-in.
      //      Actors integrate position from their slow entry velocities so
      //      they're still drifting onto the field as the countdown ticks.
      //      Input + end-checks suppressed for the whole window.
      //   2. Play  (READY_MS .. ROUND_DURATION_MS) — full control + checks.
      const elapsedFromStart = Date.now() - startedAtLocal;
      const isReady = elapsedFromStart < READY_MS;
      if (isReady) {
        // Slow entry integration — no AI, no input, no collision/exit checks.
        s.streaker.x += s.streaker.vx * dt;
        s.streaker.y += s.streaker.vy * dt;
        s.streaker.speed = _len(s.streaker.vx, s.streaker.vy);
        s.streaker.headingAngle = Math.atan2(s.streaker.vy, s.streaker.vx);
        s.leadGuard.x += s.leadGuard.vx * dt;
        s.leadGuard.y += s.leadGuard.vy * dt;
        for (let i = 0; i < s.aiGuards.length; i++) {
          const g = s.aiGuards[i];
          g.x += g.vx * dt;
          g.y += g.vy * dt;
        }
        const remainingMs = READY_MS - elapsedFromStart;
        setReadyCount(Math.max(1, Math.ceil(remainingMs / 1000)));
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      // First play-phase frame: clear the ready countdown so the
      // "STREAKER ON THE FIELD!" overlay disappears right after "1". Without
      // this the floored readyCount stays at 1 and the overlay never closes.
      // setState dedupes once it's already 0, so calling every frame is fine.
      setReadyCount(0);
      // Play phase elapsed — drives the round timeout + HUD elapsed counter.
      // Counts UP from 0; reaching ROUND_DURATION_MS = soft cap = escape.
      const playElapsedMs = elapsedFromStart - READY_MS;

      // ── Bot escalation ───────────────────────────────────────────────────
      // Every BOT_ESCALATION_INTERVAL_MS of play, AI guards gain a flat speed
      // multiplier and the lead guard's stamina pool gets partly refilled
      // (one new burst per tier). Tier transitions are detected via the
      // previous-tier counter stashed on stateRef.
      const escalationTier = Math.max(0, Math.floor(playElapsedMs / BOT_ESCALATION_INTERVAL_MS));
      if (escalationTier > (s.escalationTier ?? 0) && escalationTier > 0) {
        // Crossed a tier boundary — give the lead guard another sprint burst
        // (max-clamped so we don't blow past LEAD_GUARD_STAMINA_MAX).
        s.leadGuard.stamina = Math.min(
          LEAD_GUARD_STAMINA_MAX,
          s.leadGuard.stamina + LEAD_STAMINA_REFILL_PER_TIER,
        );
      }
      s.escalationTier = escalationTier;
      const aiSpeedMult = 1 + escalationTier * BOT_ESCALATION_PER_TIER;

      // ── Streaker physics ─────────────────────────────────────────────────
      const inp = inputRef.current;
      const ix = (inp.right ? 1 : 0) - (inp.left ? 1 : 0);
      const iy = (inp.down ? 1 : 0) - (inp.up ? 1 : 0);
      const [nx, ny] = _normalize(ix, iy);
      const sprinting = inp.sprint && s.streaker.energy > 0 && (ix !== 0 || iy !== 0);
      const targetSpeed = sprinting ? STREAKER_SPRINT_SPEED : STREAKER_BASE_SPEED;
      const desiredVx = nx * targetSpeed;
      const desiredVy = ny * targetSpeed;
      const dvx = desiredVx - s.streaker.vx;
      const dvy = desiredVy - s.streaker.vy;
      const dvm = _len(dvx, dvy);
      const maxDv = STREAKER_ACCEL * dt;
      if (dvm > maxDv) {
        s.streaker.vx += (dvx / dvm) * maxDv;
        s.streaker.vy += (dvy / dvm) * maxDv;
      } else {
        s.streaker.vx = desiredVx;
        s.streaker.vy = desiredVy;
      }
      s.streaker.x += s.streaker.vx * dt;
      s.streaker.y += s.streaker.vy * dt;
      s.streaker.x = Math.max(STREAKER_RADIUS, Math.min(w - STREAKER_RADIUS, s.streaker.x));
      s.streaker.y = Math.max(STREAKER_RADIUS, Math.min(h - STREAKER_RADIUS, s.streaker.y));
      const streakerSpeed = _len(s.streaker.vx, s.streaker.vy);
      if (streakerSpeed > 5) s.streaker.headingAngle = Math.atan2(s.streaker.vy, s.streaker.vx);
      s.streaker.speed = streakerSpeed;
      if (sprinting) s.streaker.energy = Math.max(0, s.streaker.energy - STREAKER_SPRINT_DRAIN * dt);
      else           s.streaker.energy = Math.min(STREAKER_ENERGY_MAX, s.streaker.energy + STREAKER_ENERGY_REGEN * dt);

      // ── Lead guard AI ────────────────────────────────────────────────────
      const dx = s.streaker.x - s.leadGuard.x;
      const dy = s.streaker.y - s.leadGuard.y;
      const distToStreaker = _len(dx, dy);
      const wantSprint = s.leadGuard.stamina > 0 && distToStreaker < LEAD_GUARD_SPRINT_RANGE;
      const leadSpeed = wantSprint ? LEAD_GUARD_SPRINT_SPEED : LEAD_GUARD_BASE_SPEED;
      const [ldx, ldy] = _normalize(dx, dy);
      const leadDesiredVx = ldx * leadSpeed;
      const leadDesiredVy = ldy * leadSpeed;
      const ldvx = leadDesiredVx - s.leadGuard.vx;
      const ldvy = leadDesiredVy - s.leadGuard.vy;
      const ldvm = _len(ldvx, ldvy);
      const lmaxDv = LEAD_GUARD_ACCEL * dt;
      if (ldvm > lmaxDv) {
        s.leadGuard.vx += (ldvx / ldvm) * lmaxDv;
        s.leadGuard.vy += (ldvy / ldvm) * lmaxDv;
      } else {
        s.leadGuard.vx = leadDesiredVx;
        s.leadGuard.vy = leadDesiredVy;
      }
      s.leadGuard.x += s.leadGuard.vx * dt;
      s.leadGuard.y += s.leadGuard.vy * dt;
      s.leadGuard.x = Math.max(LEAD_GUARD_RADIUS, Math.min(w - LEAD_GUARD_RADIUS, s.leadGuard.x));
      s.leadGuard.y = Math.max(LEAD_GUARD_RADIUS, Math.min(h - LEAD_GUARD_RADIUS, s.leadGuard.y));
      if (wantSprint) s.leadGuard.stamina = Math.max(0, s.leadGuard.stamina - LEAD_GUARD_STAMINA_DRAIN * dt);

      // ── AI guards ────────────────────────────────────────────────────────
      const rng = mulberry32((seed >>> 0) ^ Math.floor(now * 0.1));
      for (let i = 0; i < s.aiGuards.length; i++) {
        const g = s.aiGuards[i];
        const tx = s.streaker.x + (rng() - 0.5) * AI_GUARD_JITTER;
        const ty = s.streaker.y + (rng() - 0.5) * AI_GUARD_JITTER;
        const adx = tx - g.x;
        const ady = ty - g.y;
        const [andx, andy] = _normalize(adx, ady);
        // AI top-speed scales with the escalation tier so a long-running
        // streaker faces faster pursuers (1.0× base, +10% every 10 s). Each
        // guard then layers its OWN per-spawn speed/accel multiplier on top so
        // the pack moves at slightly different rates — no hivemind feel, and
        // no AI ever reaches the lead guard's base speed.
        const gSpeed = AI_GUARD_SPEED * aiSpeedMult * (g.speedMul ?? 1);
        const gAccel = AI_GUARD_ACCEL * (g.accelMul ?? 1);
        const aDesiredVx = andx * gSpeed;
        const aDesiredVy = andy * gSpeed;
        const advx = aDesiredVx - g.vx;
        const advy = aDesiredVy - g.vy;
        const advm = _len(advx, advy);
        const amaxDv = gAccel * dt;
        if (advm > amaxDv) {
          g.vx += (advx / advm) * amaxDv;
          g.vy += (advy / advm) * amaxDv;
        } else {
          g.vx = aDesiredVx;
          g.vy = aDesiredVy;
        }
        g.x += g.vx * dt;
        g.y += g.vy * dt;
        g.x = Math.max(GUARD_RADIUS, Math.min(w - GUARD_RADIUS, g.x));
        g.y = Math.max(GUARD_RADIUS, Math.min(h - GUARD_RADIUS, g.y));
      }

      // ── End conditions ───────────────────────────────────────────────────
      // Read the exit edge from the ref so the switcher useEffect's updates
      // are picked up without restarting the game loop.
      let outcome = null;
      if (_isAtExit(s.streaker.x, s.streaker.y, currentExitEdgeRef.current, w, h)) outcome = "escaped";
      if (!outcome) {
        const tagged = (gx, gy) => _len(gx - s.streaker.x, gy - s.streaker.y) < CATCH_DIST;
        if (tagged(s.leadGuard.x, s.leadGuard.y)) outcome = "caught";
        else if (s.aiGuards.some(g => tagged(g.x, g.y))) outcome = "caught";
      }
      if (!outcome) {
        // Soft cap — surviving the full round counts as an escape (great run
        // worthy of the leaderboard, not a timeout). Anything shorter falls
        // through and keeps ticking the HUD elapsed counter.
        if (playElapsedMs >= ROUND_DURATION_MS) outcome = "escaped";
        else setSecondsElapsed(Math.floor(playElapsedMs / 1000));
      }

      setEnergyPct(Math.round(s.streaker.energy));
      setStaminaPct(Math.round(s.leadGuard.stamina));

      if (outcome) {
        resultRef.current = outcome;
        setResult(outcome);
        // Clamp at the soft cap so a leaderboard entry doesn't record a
        // value past the design ceiling on an edge case (e.g., a late frame).
        const clampedMs = Math.max(0, Math.min(playElapsedMs, ROUND_DURATION_MS));
        try { onReportResult?.(outcome, { elapsed_ms: clampedMs }); } catch { /* swallow */ }
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(rafRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitiator, result, dims.w, dims.h, seed, exit_edge, fieldRect]);

  // Spawn confetti FX when a result lands. 👏 floats up from the bottom for
  // an escape (or 60 s survival); 👎 rains down from the top for a catch.
  // Particles auto-clear after their animation finishes plus a beat. Mirrors
  // the sim's spawnConfetti exactly so the two modes look identical.
  useEffect(() => {
    if (!result || !fieldRect) return;
    const type = result === "escaped" ? "clap" : result === "caught" ? "thumb" : null;
    if (!type) return;  // "timeout" gets neither — server-side fallback only
    const emoji = type === "clap" ? "👏" : "👎";
    const count = type === "clap" ? CONFETTI_COUNT : THUMBS_COUNT;
    const sizes = ["1rem", "1.2rem", "1.4rem", "1rem", "1.1rem"];
    const fromBottom = type === "clap";
    const particles = Array.from({ length: count }, (_, i) => ({
      id: i, emoji,
      dir:      fromBottom ? "up" : "down",
      x:        4 + Math.random() * (dims.w - 8),
      startY:   fromBottom ? dims.h + 10 : -30,
      delay:    Math.floor(Math.random() * 700),
      duration: 800 + Math.floor(Math.random() * 600),
      size:     sizes[i % sizes.length],
    }));
    setConfetti({ particles });
    const t = setTimeout(() => setConfetti(null), CONFETTI_DURATION_MS + 900);
    return () => clearTimeout(t);
  }, [result, fieldRect, dims.w, dims.h]);

  // Auto-close on result.
  // onClose is pinned to a ref so a parent re-render (game_state broadcasts
  // during end-of-game fire fast) can't reset our 2.4s hold timer. Earlier
  // code listed `onClose` in the deps — every parent render produced a new
  // arrow at the call site, the cleanup cleared the pending timeout, and the
  // CAUGHT overlay stuck on screen indefinitely.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  useEffect(() => {
    if (!result) return;
    const t = setTimeout(() => onCloseRef.current?.(), RESULT_HOLD_MS);
    return () => clearTimeout(t);
  }, [result]);

  // Exit-edge switcher — re-rolls the escape edge every few seconds during
  // the run. On each switch, optionally spawns one extra AI guard from the
  // side that just CLOSED (the previous exit edge), adding pressure on the
  // player who was racing toward that exit. Initiator only.
  // dims via ref so a mid-game resize doesn't reset the scheduler.
  const dimsRef = useRef(dims);
  useEffect(() => { dimsRef.current = dims; }, [dims]);
  useEffect(() => {
    if (!isInitiator || result) return;
    let timeoutId = 0;
    let cancelled = false;
    let reinforcementsSpawned = 0;
    const scheduleNext = () => {
      const delay = EXIT_SWITCH_MIN_MS + Math.random() * (EXIT_SWITCH_MAX_MS - EXIT_SWITCH_MIN_MS);
      timeoutId = setTimeout(() => {
        if (cancelled) return;
        // Read the previous edge from the ref (kept in sync via a separate
        // effect) so we can spawn a reinforcement from it without putting a
        // side effect inside the setState updater — React 18 strict mode can
        // double-invoke updaters, which would double-write a closure var.
        const closedEdge = currentExitEdgeRef.current;
        setCurrentExitEdge(prev => {
          const choices = ["top", "right", "bottom", "left"].filter(e => e !== prev);
          return choices[Math.floor(Math.random() * choices.length)];
        });
        // Reinforcement: one fresh guard glides in from the edge that just
        // stopped being the exit. Bounded by MAX_REINFORCEMENTS to keep the
        // pre-reserved render slots safe.
        if (REINFORCEMENT_ON_SWITCH && closedEdge && reinforcementsSpawned < MAX_REINFORCEMENTS) {
          const s = stateRef.current;
          const { w, h } = dimsRef.current || { w: 600, h: 500 };
          if (s && s.aiGuards) {
            // Seed off the run seed + spawn index so positions are stable per run.
            const rng = mulberry32((seed >>> 0) ^ (0xA5A5A5A5 + reinforcementsSpawned));
            const pos = _offFieldGuardPos(closedEdge, w, h, rng);
            const [vx, vy] = _inwardVelocity(closedEdge, ENTRY_SPEED_GUARDS);
            const { speedMul, accelMul } = _rollGuardMultipliers(rng);
            s.aiGuards.push({ x: pos.x, y: pos.y, vx, vy, speedMul, accelMul });
            reinforcementsSpawned += 1;
          }
        }
        scheduleNext();
      }, delay);
    };
    // Wait for the ready/entry window to finish before the first switch so the
    // band doesn't jump while everyone is still gliding in.
    const introDoneTimer = setTimeout(scheduleNext, READY_MS);
    return () => {
      cancelled = true;
      clearTimeout(introDoneTimer);
      clearTimeout(timeoutId);
    };
  }, [isInitiator, result, seed]);

  // Spectator timer (no game loop). Mirrors the initiator's two-phase clock
  // so both viewers see the same numbers — countdown digit during ready,
  // then UP-counter once the streak begins.
  useEffect(() => {
    if (isInitiator) return;
    const startMs = startedAt || Date.now();
    const tick = () => {
      const elapsedFromStart = Date.now() - startMs;
      if (elapsedFromStart < READY_MS) {
        setReadyCount(Math.max(1, Math.ceil((READY_MS - elapsedFromStart) / 1000)));
        setSecondsElapsed(0);
        return;
      }
      const playElapsed = elapsedFromStart - READY_MS;
      setReadyCount(0);
      setSecondsElapsed(Math.max(0, Math.min(Math.floor(playElapsed / 1000), Math.floor(ROUND_DURATION_MS / 1000))));
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [isInitiator, startedAt]);

  // ── Render ──────────────────────────────────────────────────────────────
  // Use `currentExitEdge` (state) so the arrow + tinted band re-render every
  // time the switcher useEffect re-rolls the edge mid-run.
  const arrowGlyph = useMemo(() => {
    switch (currentExitEdge) {
      case "top":    return "↑";
      case "bottom": return "↓";
      case "left":   return "←";
      case "right":  return "→";
      default:       return "→";
    }
  }, [currentExitEdge]);

  const exitBandStyle = useMemo(() => {
    const { w, h } = dims;
    switch (currentExitEdge) {
      case "top":    return { left: 0, top: 0,              width: w, height: EXIT_BAND_PX, borderBottom: "2px dashed rgba(251,191,36,0.6)" };
      case "bottom": return { left: 0, top: h - EXIT_BAND_PX, width: w, height: EXIT_BAND_PX, borderTop:    "2px dashed rgba(251,191,36,0.6)" };
      case "left":   return { left: 0, top: 0,              width: EXIT_BAND_PX, height: h, borderRight:  "2px dashed rgba(251,191,36,0.6)" };
      case "right":  return { left: w - EXIT_BAND_PX, top: 0, width: EXIT_BAND_PX, height: h, borderLeft:   "2px dashed rgba(251,191,36,0.6)" };
      default:       return { display: "none" };
    }
  }, [currentExitEdge, dims]);

  // No field detected — bail with a tiny fallback so the modal doesn't
  // silently swallow input. (Shouldn't happen in normal play.)
  if (!fieldRect) {
    return (
      <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/70 text-white">
        <p>Streaker field unavailable</p>
      </div>
    );
  }

  return (
    <div
      className={`pointer-events-none z-[1100] overflow-hidden ${embedded ? "absolute inset-0" : "fixed"}`}
      style={embedded ? undefined : { top: fieldRect.top, left: fieldRect.left, width: fieldRect.width, height: fieldRect.height }}
    >
      {/* Exit band — tinted strip with dashed line on the exit edge. Drawn
          before the characters so the streaker/guards always paint on top. */}
      <div className="absolute bg-amber-500/15" style={exitBandStyle} />

      {/* Exit arrow indicator — big pulsing glyph on the (current) exit edge.
          Drawn BELOW the characters so a streaker crossing into the exit zone
          renders on top of it instead of being covered the instant they win. */}
      <ExitArrow exit_edge={currentExitEdge} glyph={arrowGlyph} />

      {/* Characters (initiator) or placeholder (spectator) */}
      {isInitiator ? (
        <Characters stateRef={stateRef} resultRef={resultRef} />
      ) : (
        <SpectatorPlaceholder initiatorName={initiatorName} dims={dims} />
      )}

      {/* HUD strip — sits inside the field at the very top, translucent so the
          gameboard remains visible. Pointer-events-none lets clicks through to
          the gameboard if needed. */}
      <div className="absolute left-0 right-0 top-0 flex items-center justify-between px-2 py-1 bg-black/55 backdrop-blur-sm border-b border-slate-700/70 text-[10px] pointer-events-none">
        <div className="flex items-center gap-1.5">
          <span className="font-black uppercase tracking-widest text-amber-200">Streaker</span>
          <div className="w-24 h-1.5 rounded-full bg-slate-900/80 border border-slate-700 overflow-hidden">
            <div className="h-full bg-emerald-400 transition-[width] duration-100" style={{ width: `${(energyPct / STREAKER_ENERGY_MAX) * 100}%` }} />
          </div>
        </div>
        {/* Count-UP elapsed timer. Switches color as the soft cap approaches —
            amber when escalation is biting (≥40s), red+pulse in the final 5s
            before the survival escape triggers. */}
        <div className={`font-mono font-black tabular-nums text-base ${
          secondsElapsed >= 55 ? "text-red-400 animate-pulse"
          : secondsElapsed >= 40 ? "text-amber-300"
          : "text-slate-100"
        }`}>
          {secondsElapsed}s
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-24 h-1.5 rounded-full bg-slate-900/80 border border-slate-700 overflow-hidden">
            <div className="h-full bg-rose-400 transition-[width] duration-100" style={{ width: `${(staminaPct / LEAD_GUARD_STAMINA_MAX) * 100}%` }} />
          </div>
          <span className="font-black uppercase tracking-widest text-rose-300">Lead</span>
        </div>
      </div>

      {/* Pre-streak ready countdown — "STREAKER ON THE FIELD!" with 3-2-1.
          Renders on top of everything except the result overlay (which can't
          appear until play has begun anyway). Keyed by `readyCount` so each
          digit animates fresh as it ticks down. */}
      {readyCount > 0 && !result && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/45 backdrop-blur-[2px] pointer-events-none">
          <p className="text-amber-200 font-black uppercase tracking-[0.25em] text-lg md:text-2xl drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
            Streaker on the field!
          </p>
          <p
            key={readyCount}
            className="mt-2 text-7xl md:text-8xl font-black text-white tabular-nums drop-shadow-[0_4px_12px_rgba(0,0,0,0.85)] animate-[ping_700ms_ease-out]"
            style={{ animationIterationCount: 1 }}
          >
            {readyCount}
          </p>
        </div>
      )}

      {/* Result overlay */}
      {result && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm pointer-events-none">
          <div className={`px-6 py-4 rounded-2xl border-2 shadow-2xl text-center ${
            result === "escaped"
              ? "border-emerald-400 bg-emerald-500/15"
              : result === "caught"
              ? "border-rose-400 bg-rose-500/15"
              : "border-slate-400 bg-slate-500/15"
          }`}>
            <p className={`text-3xl font-black ${
              result === "escaped" ? "text-emerald-300" : result === "caught" ? "text-rose-300" : "text-slate-300"
            }`}>
              {result === "escaped" ? "ESCAPED!" : result === "caught" ? "CAUGHT!" : "TIME'S UP"}
            </p>
          </div>
        </div>
      )}

      {/* Confetti FX — 👏 on escape, 👎 on catch. Layered ABOVE the result overlay. */}
      {confetti && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {confetti.particles.map(p => <ConfettiParticle key={p.id} {...p} />)}
        </div>
      )}

      {/* Keyframes for the confetti particles. Matches StreakerEffect's
          `streaker-fall-down` / `streaker-fall-up` so the celebration FX
          looks identical between modes. */}
      <style>{`
        @keyframes streaker-fall-down {
          0%   { opacity: 0; transform: translateY(0)     rotate(0deg); }
          10%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { opacity: 0; transform: translateY(120px)  rotate(22deg); }
        }
        @keyframes streaker-fall-up {
          0%   { opacity: 0; transform: translateY(0)     rotate(0deg); }
          10%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { opacity: 0; transform: translateY(-120px) rotate(-22deg); }
        }
      `}</style>
    </div>
  );
}

// Confetti emoji particle — port of StreakerEffect's ConfettiParticle.
function ConfettiParticle({ emoji, x, startY, delay, duration, size, dir }) {
  return (
    <div
      className="pointer-events-none absolute select-none"
      style={{
        left: x, top: startY, fontSize: size,
        animation: `streaker-fall-${dir} ${duration}ms ${delay}ms ease-in forwards`,
        opacity: 0,
      }}
    >
      {emoji}
    </div>
  );
}

// ── Dot rendering — RAF-driven DOM writes, never React state per frame ──────
function Characters({ stateRef, resultRef }) {
  const streakerRef = useRef(null);
  const buttDot1Ref = useRef(null);
  const buttDot2Ref = useRef(null);
  const leadGuardRef = useRef(null);
  const leadGuardRingRef = useRef(null);
  const aiRefs = useRef([]);
  useEffect(() => {
    let raf = 0;
    const startedAt = performance.now();
    const draw = (now) => {
      const s = stateRef.current;
      if (s) {
        // Streaker dot
        if (streakerRef.current) {
          streakerRef.current.style.transform = `translate(${s.streaker.x - STREAKER_RADIUS}px, ${s.streaker.y - STREAKER_RADIUS}px)`;
        }
        // Butt dots
        if (buttDot1Ref.current && buttDot2Ref.current) {
          const a = s.streaker.headingAngle ?? 0;
          const bx = -Math.cos(a);
          const by = -Math.sin(a);
          const px = -Math.sin(a);
          const py =  Math.cos(a);
          const cx1 = s.streaker.x + bx * BUTT_DOT_BACK_DIST + px * BUTT_DOT_SPREAD - BUTT_DOT_RADIUS;
          const cy1 = s.streaker.y + by * BUTT_DOT_BACK_DIST + py * BUTT_DOT_SPREAD - BUTT_DOT_RADIUS;
          const cx2 = s.streaker.x + bx * BUTT_DOT_BACK_DIST - px * BUTT_DOT_SPREAD - BUTT_DOT_RADIUS;
          const cy2 = s.streaker.y + by * BUTT_DOT_BACK_DIST - py * BUTT_DOT_SPREAD - BUTT_DOT_RADIUS;
          const vmag = s.streaker.speed ?? 0;
          const speedRatio = Math.min(vmag / BUTT_JIGGLE_SPEED_REF, 1);
          const liveFreq = vmag < 1 ? 0 : BUTT_JIGGLE_FREQ_SLOW + speedRatio * (BUTT_JIGGLE_FREQ_MAX - BUTT_JIGGLE_FREQ_SLOW);
          const liveAmp  = vmag < 1 ? 0 : BUTT_JIGGLE_AMP_SLOW  + speedRatio * (BUTT_JIGGLE_AMP_FAST  - BUTT_JIGGLE_AMP_SLOW);
          const elapsedMs = now - startedAt;
          const wave = Math.sin(2 * Math.PI * liveFreq * elapsedMs * 0.001);
          const s1 = 1 - liveAmp * Math.max(0,  wave);
          const s2 = 1 - liveAmp * Math.max(0, -wave);
          buttDot1Ref.current.style.transform = `translate(${cx1}px, ${cy1}px) scale(${s1})`;
          buttDot2Ref.current.style.transform = `translate(${cx2}px, ${cy2}px) scale(${s2})`;
        }
        // Lead guard + its glow ring (highlight)
        if (leadGuardRef.current) {
          leadGuardRef.current.style.transform = `translate(${s.leadGuard.x - LEAD_GUARD_RADIUS}px, ${s.leadGuard.y - LEAD_GUARD_RADIUS}px)`;
        }
        if (leadGuardRingRef.current) {
          // Ring is larger than the dot — sits behind, pulses with stamina.
          const RING_R = LEAD_GUARD_RADIUS + 6;
          leadGuardRingRef.current.style.transform = `translate(${s.leadGuard.x - RING_R}px, ${s.leadGuard.y - RING_R}px)`;
          leadGuardRingRef.current.style.opacity = String(s.leadGuard.stamina > 0 ? 1 : 0.4);
        }
        // AI guards
        for (let i = 0; i < s.aiGuards.length; i++) {
          const el = aiRefs.current[i];
          if (el) el.style.transform = `translate(${s.aiGuards[i].x - GUARD_RADIUS}px, ${s.aiGuards[i].y - GUARD_RADIUS}px)`;
        }
      }
      if (!resultRef.current) raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [stateRef, resultRef]);

  const offBaseStyle = { position: "absolute", top: 0, left: 0, willChange: "transform", transform: "translate(-200px,-200px)" };

  return (
    <>
      {/* Butt dots render first so the main streaker layers on top. */}
      <div ref={buttDot1Ref} style={{ ...offBaseStyle, width: BUTT_DOT_SIZE_PX, height: BUTT_DOT_SIZE_PX }} className="rounded-full bg-amber-100 pointer-events-none" />
      <div ref={buttDot2Ref} style={{ ...offBaseStyle, width: BUTT_DOT_SIZE_PX, height: BUTT_DOT_SIZE_PX }} className="rounded-full bg-amber-100 pointer-events-none" />
      {/* Streaker — same 14 px amber dot as the sim. */}
      <div ref={streakerRef} style={{ ...offBaseStyle, width: STREAKER_DIAMETER, height: STREAKER_DIAMETER }} className="rounded-full bg-amber-100 pointer-events-none" />
      {/* Lead guard highlight ring — sits BEHIND the lead guard dot. The
          opponent will steer this guard in v2, so it needs to read as "the
          one to watch" at a glance. */}
      <div
        ref={leadGuardRingRef}
        style={{ ...offBaseStyle, width: (LEAD_GUARD_RADIUS + 6) * 2, height: (LEAD_GUARD_RADIUS + 6) * 2 }}
        className="rounded-full border-2 border-rose-400/80 ring-2 ring-rose-400/30 animate-pulse pointer-events-none"
      />
      {/* Lead guard dot — slightly larger sky-blue, same family as AI guards. */}
      <div ref={leadGuardRef} style={{ ...offBaseStyle, width: LEAD_GUARD_DIAMETER, height: LEAD_GUARD_DIAMETER }} className="rounded-full bg-sky-200 pointer-events-none" />
      {/* AI guards — base count plus pre-reserved render slots for any
          reinforcements spawned mid-run by the exit-switcher. Slots stay
          parked off-base (transform: translate(-200,-200)) until the game
          loop assigns them a position. */}
      {[...Array(AI_GUARD_COUNT + MAX_REINFORCEMENTS)].map((_, i) => (
        <div
          key={i}
          ref={el => { aiRefs.current[i] = el; }}
          style={{ ...offBaseStyle, width: GUARD_DIAMETER, height: GUARD_DIAMETER }}
          className="rounded-full bg-sky-300/95 pointer-events-none"
        />
      ))}
    </>
  );
}

function SpectatorPlaceholder({ initiatorName, dims }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 pointer-events-none"
         style={{ width: dims.w, height: dims.h }}>
      <span className="text-5xl">🏃</span>
      <p className="text-base font-black text-amber-200 drop-shadow-[0_0_6px_rgba(0,0,0,0.6)]">
        {initiatorName ? `${initiatorName} is streaking!` : "Streaker on the field!"}
      </p>
    </div>
  );
}

function ExitArrow({ exit_edge, glyph }) {
  const style = (() => {
    const offset = 18;
    switch (exit_edge) {
      case "top":    return { left: "50%", top: offset, transform: "translateX(-50%)" };
      case "bottom": return { left: "50%", bottom: offset, transform: "translateX(-50%)" };
      case "left":   return { left: offset, top: "50%", transform: "translateY(-50%)" };
      case "right":  return { right: offset, top: "50%", transform: "translateY(-50%)" };
      default:       return {};
    }
  })();
  return (
    <div
      className="absolute text-amber-300 font-black drop-shadow-[0_0_8px_rgba(251,191,36,0.7)] animate-pulse pointer-events-none"
      style={{ ...style, fontSize: 38 }}
    >
      {glyph}
    </div>
  );
}
